import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FilterIcon, ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type SortBy = 'candidate_name' | 'created_at' | 'ended_at' | 'due_at';
type SortOrder = 'asc' | 'desc';

interface SessionSortingFilterPopoverProps {
    sortBy: SortBy;
    sortOrder: SortOrder;
    onSortChange: (sortBy: SortBy, sortOrder: SortOrder) => void;
    statusFilter?: ('incomplete' | 'complete' | 'graded')[];
    disabled?: boolean;
}

const SORT_OPTIONS: { value: SortBy; label: string }[] = [
    { value: 'candidate_name', label: 'Candidate Name' },
    { value: 'created_at', label: 'Created At' },
    { value: 'ended_at', label: 'Ended At' },
    { value: 'due_at', label: 'Due At' },
];

export function SessionSortingFilterPopover({
    sortBy,
    sortOrder,
    onSortChange,
    statusFilter = [],
    disabled = false
}: SessionSortingFilterPopoverProps) {
    const [open, setOpen] = useState(false);

    const handleSortClick = (newSortBy: SortBy) => {
        // Only change the sort field, keep current order
        onSortChange(newSortBy, sortOrder);
        // Remove setOpen(false) to keep popover open when clicking badges
    };

    const handleOrderToggle = () => {
        // Toggle between asc and desc for current sort field
        const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        onSortChange(sortBy, newOrder);
    };

    // Check if a sort option should be disabled
    const isSortOptionDisabled = (optionValue: SortBy) => {
        // Disable "Ended At" when filtering by incomplete sessions
        return optionValue === 'ended_at' && statusFilter.includes('incomplete');
    };

    // Check if current sort field is disabled
    const isCurrentSortDisabled = isSortOptionDisabled(sortBy);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" disabled={disabled}>
                    <FilterIcon className="h-4 w-4" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="end">
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">Sort by</h4>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleOrderToggle}
                            disabled={isCurrentSortDisabled}
                            className="h-7 px-2 text-xs"
                        >
                            {sortOrder === 'asc' ? (
                                <>
                                    <ChevronUpIcon className="h-3 w-3 mr-1" />
                                    Asc
                                </>
                            ) : (
                                <>
                                    <ChevronDownIcon className="h-3 w-3 mr-1" />
                                    Desc
                                </>
                            )}
                        </Button>
                    </div>
                    <div className="space-y-1">
                        {SORT_OPTIONS.map((option) => {
                            const isDisabled = isSortOptionDisabled(option.value);
                            const isSelected = sortBy === option.value && !isDisabled;

                            return (
                                <Badge
                                    key={option.value}
                                    variant={isSelected ? "default" : "secondary"}
                                    className={cn(
                                        "w-full justify-center cursor-pointer transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 py-2",
                                        isDisabled && "opacity-50 cursor-not-allowed",
                                        isSelected
                                            ? "hover:bg-primary/90"
                                            : !isDisabled && "hover:bg-card hover:border-border/50"
                                    )}
                                    onClick={() => !isDisabled && handleSortClick(option.value)}
                                >
                                    <span className={cn("text-xs", isDisabled && "text-muted-foreground")}>
                                        {option.label}
                                    </span>
                                </Badge>
                            );
                        })}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
